import { ActionListItem, ActionListBusyIndicator } from "client/Components";
import { useFollowAction } from "client/Features/Hub/Announcements/AnnouncementActions/useFollowAction";
import { AnnouncementStatisticsModel, getStatistics } from "client/Api/Hub/Announcements/announcementViewer";
import { ReactElement, useCallback, useEffect, useState } from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { AnnouncementCommentNotificationDataViewModel } from "client/Api/Notifications/notifications";

type LoadingResult =
	{
		wasSuccessful: false;
	}
	|
	{
		wasSuccessful: true;
		isFollowing: boolean;
	};

export type AnnouncementCommentActionsProps = {
	data: AnnouncementCommentNotificationDataViewModel;
};

export const AnnouncementCommentActions = (props: AnnouncementCommentActionsProps): ReactElement | null => {
	const {
		data
	} = props;

	const { announcementId } = data;

	const [loadingResult, setLoadingResult] = useState<LoadingResult | null>(null);

	useEffect(
		() => {

			const load = async (announcementId: number, signal: AbortSignal): Promise<void> => {
				try {
					const { isCurrentUserFollowingComments } = await getStatistics(announcementId, signal);
					setLoadingResult({
						wasSuccessful: true,
						isFollowing: isCurrentUserFollowingComments
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);

					setLoadingResult({
						wasSuccessful: false
					});
				}
			};

			const cancellation = new AbortController();
			load(announcementId, cancellation.signal);
			return () => cancellation.abort();
		},
		[announcementId]
	);

	const isFollowingChangeHandler = useCallback(
		(isFollowing: boolean): void => {
			setLoadingResult({
				wasSuccessful: true,
				isFollowing
			});
		},
		[]
	);

	if (loadingResult === null) {
		return (
			<ActionListBusyIndicator />
		);
	}

	if (!loadingResult.wasSuccessful) {
		return null;
	}

	return (
		<FollowActionListItem
			announcementId={announcementId}
			isFollowing={loadingResult.isFollowing}
			onIsFollowingChanged={isFollowingChangeHandler}
		/>
	);
};
AnnouncementCommentActions.displayName = "AnnouncementCommentActions";

type FollowActionListItemProps = {
	announcementId: number;
	isFollowing: boolean;
	onIsFollowingChanged: (isFollowing: boolean) => void;
};

const FollowActionListItem = (props: FollowActionListItemProps): ReactElement => {
	const {
		announcementId,
		isFollowing,
		onIsFollowingChanged
	} = props;

	const statisticsChangeHandler = useCallback(
		(statistics: AnnouncementStatisticsModel): void => {
			onIsFollowingChanged(statistics.isCurrentUserFollowingComments);
		},
		[onIsFollowingChanged]
	);

	const [icon, key, clickHandler] = useFollowAction(
		announcementId,
		isFollowing,
		statisticsChangeHandler
	);

	return (
		<ActionListItem
			icon={icon}
			text={localize(key)}
			onClick={clickHandler}
			trackingId={isFollowing ? "unfollow" : "follow"}
		/>
	);
};
FollowActionListItem.displayName = "FollowActionListItem";